import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './TermTooltip.css';

const OPEN_EVENT = 'term-tooltip-open';

export default function TermTooltip({ term, definition, why, children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const idRef = useRef(Math.random());

  const close = useCallback(() => setOpen(false), []);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (open) { close(); return; }

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 300;
    const tooltipHeight = 160; // approx — enough to detect overflow
    const left = Math.min(rect.left, window.innerWidth - tooltipWidth - 16);
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < tooltipHeight + 16
      ? rect.top + window.scrollY - tooltipHeight - 6  // flip above
      : rect.bottom + 6 + window.scrollY;
    setPos({ top, left });

    // close every other tooltip first
    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { id: idRef.current } }));
    setOpen(true);
  }, [open, close]);

  useEffect(() => {
    // close when another tooltip opens
    const onOtherOpen = (e) => { if (e.detail.id !== idRef.current) close(); };
    document.addEventListener(OPEN_EVENT, onOtherOpen);
    // close on outside click
    const onClickOut = () => close();
    document.addEventListener('click', onClickOut);
    return () => {
      document.removeEventListener(OPEN_EVENT, onOtherOpen);
      document.removeEventListener('click', onClickOut);
    };
  }, [close]);

  return (
    <>
      <span
        ref={triggerRef}
        className={`term-trigger${open ? ' term-trigger--active' : ''}`}
        onClick={handleClick}
      >
        {children}
      </span>
      {open && createPortal(
        <div
          className="term-popup"
          style={{ top: pos.top, left: pos.left }}
          onClick={e => e.stopPropagation()}
        >
          <div className="term-popup__name">{term}</div>
          <div className="term-popup__def">{definition}</div>
          {why && <div className="term-popup__why">{why}</div>}
        </div>,
        document.body
      )}
    </>
  );
}
